import { useState, useEffect, useRef } from 'react';
import { Button, Input, NumberInput } from '@atoms/index';
import { useRestaurantStore } from '@store/restaurantStore';
import type { Category } from '@services/menuService';
import { cn } from '@/utils/cn';
import { storage, STORAGE_KEYS } from '@/utils/storage';
import { compressImage } from '@utils/compressImage';
import type { AuthTokens } from '@/types';

interface CategoryFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  category?: Category | null;
  onSuccess: () => void;
}

/**
 * CategoryFormModal - Create/edit category modal
 *
 * Per Restaurant Management Panel: Menu management
 * Follows pattern from AddAddressModal.tsx
 */
const CategoryFormModal = ({
  isOpen,
  onClose,
  category,
  onSuccess,
}: CategoryFormModalProps) => {
  const { createCategory, updateCategory } = useRestaurantStore();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    imageUrl: '',
    displayOrder: 0,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  // Initialize form data
  useEffect(() => {
    if (category) {
      setFormData({
        name: category.name || '',
        description: category.description || '',
        imageUrl: category.imageUrl || '',
        displayOrder: category.displayOrder || 0,
      });
    } else {
      setFormData({
        name: '',
        description: '',
        imageUrl: '',
        displayOrder: 0,
      });
    }
  }, [category, isOpen]);

  const handleInputChange = (field: string, value: string | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.files?.[0];
    if (!raw) return;
    e.target.value = '';

    try {
      const { file } = await compressImage(raw, { maxWidthOrHeight: 800, maxSizeMB: 0.5 });

      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', 'categories');

      const tokens = storage.get<AuthTokens>(STORAGE_KEYS.AUTH_TOKENS);
      const accessToken = tokens?.access_token;

      if (!accessToken) {
        setError('Oturum süreniz dolmuş. Lütfen tekrar giriş yapın.');
        return;
      }

      const response = await fetch(`${import.meta.env.VITE_API_URL || '/api'}/upload/image`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || 'Resim yüklenirken bir hata oluştu');
      }

      const result = await response.json();
      if (result.success && result.data?.url) {
        setFormData((prev) => ({ ...prev, imageUrl: result.data.url }));
        setError(null);
      } else {
        setError('Resim yüklenirken bir hata oluştu');
      }
    } catch (err: any) {
      console.error('Image upload error:', err);
      setError(err.message || 'Resim yüklenirken bir hata oluştu');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      if (category) {
        await updateCategory(category.id, formData);
      } else {
        await createCategory(formData);
      }
      onSuccess();
      // Close modal after successful save
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Kategori kaydedilirken bir hata oluştu');
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              {category ? 'Kategori Düzenle' : 'Yeni Kategori'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              ✕
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Kategori Adı *
              </label>
              <Input
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Kategori adı"
                required
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Açıklama
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Kategori açıklaması"
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            {/* Image */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Görsel
              </label>
              {formData.imageUrl ? (
                <div className="space-y-2">
                  <div className="w-full max-w-xs aspect-[4/3] rounded-lg overflow-hidden border-2 border-gray-200 bg-gray-50 flex items-center justify-center">
                    <img
                      src={`${formData.imageUrl}?t=${Date.now()}`}
                      alt="Category preview"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="primary"
                      onClick={() => imageInputRef.current?.click()}
                    >
                      🔄 Görseli Değiştir
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={() => {
                        setFormData((prev) => ({ ...prev, imageUrl: '' }));
                        if (imageInputRef.current) {
                          imageInputRef.current.value = '';
                        }
                      }}
                    >
                      ✕ Kaldır
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="w-full h-48 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 flex items-center justify-center">
                    <div className="text-center text-gray-400">
                      <p className="text-sm mb-2">📷 Görsel yüklenmedi</p>
                      <p className="text-xs">Kategori görseli için resim yükleyin</p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="primary"
                    onClick={() => imageInputRef.current?.click()}
                  >
                    📤 Görsel Yükle
                  </Button>
                </div>
              )}
              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
            </div>

            {/* Display Order */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sıralama (düşük = öncelikli)
              </label>
              <NumberInput
                integerOnly
                value={formData.displayOrder === 0 ? '' : String(formData.displayOrder)}
                onChange={(s) => handleInputChange('displayOrder', s === '' ? 0 : parseInt(s, 10) || 0)}
                placeholder="0"
              />
            </div>

            {/* Error */}
            {error && (
              <div className="bg-error/10 border border-error/20 rounded-lg p-3">
                <p className="text-error text-sm">{error}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-4 justify-end">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={onClose}
              >
                İptal
              </Button>
              <Button
                type="submit"
                variant="primary"
                size="sm"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Kaydediliyor...' : category ? 'Güncelle' : 'Oluştur'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CategoryFormModal;
